import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid, Legend,
} from "recharts";
import Breadcrumb from "../common/breadcrumb";
import { Container } from "reactstrap";
import { C, PIE_COLORS, CHART_LINE, tbl, reportingGlobalCSS } from "./reportingStyles";
import { GradientDefs, ChartTooltip, Tile, RCard, Badge, Loader } from "./ReportingShared";
import { getReportingProductPerformance, fetchBranches } from "../../utils/apiCalls";

// ─── Date preset helpers ─────────────────────────────────
const today = () => new Date().toISOString().split("T")[0];
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split("T")[0]; };
const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().split("T")[0]; };
const yearStart = () => `${new Date().getFullYear()}-01-01`;

const DATE_PRESETS = [
  { label: "Today", value: "today", range: () => [today(), today()] },
  { label: "Last 7 Days", value: "7d", range: () => [daysAgo(7), today()] },
  { label: "Last 30 Days", value: "30d", range: () => [daysAgo(30), today()] },
  { label: "This Month", value: "month", range: () => [monthStart(), today()] },
  { label: "This Year", value: "year", range: () => [yearStart(), today()] },
  { label: "All Time", value: "all", range: () => [null, null] },
  { label: "Custom", value: "custom", range: () => [null, null] },
];

// ─── Custom react-select styles ──────────────────────────
const selectStyles = {
  control: (base, state) => ({
    ...base, minHeight: 36, borderRadius: 10, borderColor: state.isFocused ? C.coral : C.border,
    boxShadow: state.isFocused ? `0 0 0 1px ${C.coral}` : "none", fontSize: 13, fontFamily: "inherit",
    "&:hover": { borderColor: C.coral },
  }),
  option: (base, state) => ({
    ...base, fontSize: 13, padding: "8px 12px",
    backgroundColor: state.isSelected ? C.coralLight : state.isFocused ? "#f0fdfa" : "white",
    color: state.isSelected ? C.coral : C.text,
  }),
  multiValue: (base) => ({ ...base, borderRadius: 6, background: C.coralLight }),
  multiValueLabel: (base) => ({ ...base, color: C.coral, fontWeight: 600, fontSize: 12 }),
  multiValueRemove: (base) => ({ ...base, color: C.coral, ":hover": { background: C.coral, color: "white" } }),
  placeholder: (base) => ({ ...base, color: C.textLight, fontSize: 13 }),
  menu: (base) => ({ ...base, borderRadius: 10, boxShadow: C.shadowMd, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const ProductPerformancePage = () => {
  const userType = localStorage.getItem("userType");
  const merchantIdFromStorage = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantIdFromStorage : reduxSelectedMerchant;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [catFilter, setCatFilter] = useState("All");
  const [expandedId, setExpandedId] = useState(null);

  // Filter state
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [datePreset, setDatePreset] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Load branches when merchant changes
  useEffect(() => {
    if (!selectedMerchant || selectedMerchant === "all") return;
    (async () => {
      const res = await fetchBranches({ merchantId: selectedMerchant });
      const branches = res?.data || [];
      setBranchOptions(branches.map(b => ({ value: b.id, label: b.name })));
      setSelectedBranches([]);
    })();
  }, [selectedMerchant]);

  const fetchData = useCallback(async () => {
    if (!selectedMerchant || selectedMerchant === "all") return;
    setLoading(true);
    try {
      const filters = {};
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      if (selectedBranches.length > 0) {
        filters.branchIds = selectedBranches.map(b => b.value).join(",");
      }
      const res = await getReportingProductPerformance(selectedMerchant, filters);
      if (res) setData(res);
    } catch (e) { console.error("Failed to fetch product performance:", e); }
    setLoading(false);
  }, [selectedMerchant, startDate, endDate, selectedBranches]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Handle date preset change
  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    const found = DATE_PRESETS.find(p => p.value === preset);
    if (found && preset !== "custom") {
      const [s, e] = found.range();
      setStartDate(s || "");
      setEndDate(e || "");
    }
  };

  if (!selectedMerchant || selectedMerchant === "all") {
    return (
      <Fragment>
        <Breadcrumb title="Product Performance" parent="Reporting" />
        <Container fluid>
          <div className="rpt-wrap" style={{ padding: 40, textAlign: "center", color: C.textMed }}>
            <style>{reportingGlobalCSS}</style>
            Please select a merchant to view reporting data.
          </div>
        </Container>
      </Fragment>
    );
  }

  const products = data?.products || [];
  const productDaily = data?.productDaily || [];
  const top4Names = data?.top4Names || [];
  const branchComparison = data?.branchComparison || [];

  const totalQty = products.reduce((s, p) => s + p.qtySold, 0);
  const totalRev = products.reduce((s, p) => s + p.revenue, 0);
  const topQ = [...products].sort((a, b) => b.qtySold - a.qtySold)[0];
  const topR = [...products].sort((a, b) => b.revenue - a.revenue)[0];
  const categories = [...new Set(products.map(p => p.category))];
  const catData = categories.map(cat => ({ name: cat, value: products.filter(p => p.category === cat).reduce((s, p) => s + p.revenue, 0) }));
  const filteredP = catFilter === "All" ? products : products.filter(p => p.category === catFilter);

  return (
    <Fragment>
      <Breadcrumb title="Product Performance" parent="Reporting" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">

          {/* ─── Filter Bar ─────────────────────────────── */}
          <RCard style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
              {/* Date Preset */}
              <div style={{ minWidth: 140 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Date Range</div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {DATE_PRESETS.filter(p => p.value !== "custom").map(p => (
                    <div key={p.value} onClick={() => handlePresetChange(p.value)}
                      style={{
                        padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: datePreset === p.value ? 700 : 500,
                        cursor: "pointer", border: datePreset === p.value ? `1.5px solid ${C.coral}` : `1.5px solid ${C.border}`,
                        background: datePreset === p.value ? C.coralLight : C.white,
                        color: datePreset === p.value ? C.coral : C.textMed, transition: "all 0.12s", whiteSpace: "nowrap",
                      }}>{p.label}</div>
                  ))}
                </div>
              </div>

              {/* Custom date inputs */}
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>From</div>
                  <input type="date" value={startDate}
                    onChange={e => { setStartDate(e.target.value); setDatePreset("custom"); }}
                    style={{ padding: "7px 10px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 12, fontFamily: "inherit", outline: "none", color: C.text, transition: "border 0.15s" }}
                    onFocus={e => e.target.style.borderColor = C.coral} onBlur={e => e.target.style.borderColor = C.border}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>To</div>
                  <input type="date" value={endDate}
                    onChange={e => { setEndDate(e.target.value); setDatePreset("custom"); }}
                    style={{ padding: "7px 10px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 12, fontFamily: "inherit", outline: "none", color: C.text, transition: "border 0.15s" }}
                    onFocus={e => e.target.style.borderColor = C.coral} onBlur={e => e.target.style.borderColor = C.border}
                  />
                </div>
              </div>

              {/* Branch multi-select */}
              <div style={{ minWidth: 240, flex: 1, maxWidth: 400 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Branches</div>
                <Select
                  isMulti
                  options={branchOptions}
                  value={selectedBranches}
                  onChange={val => setSelectedBranches(val || [])}
                  placeholder="All Branches"
                  styles={selectStyles}
                  isClearable
                  closeMenuOnSelect={false}
                  menuPortalTarget={document.body}
                />
              </div>
            </div>
          </RCard>

          {loading ? <Loader /> : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 24 }}>
                <Tile label="Items Sold" value={totalQty} sub="This period" icon="📦" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                <Tile label="Product Revenue" value={`${totalRev} SAR`} sub="Total sales" icon="💰" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={80} />
                <Tile label="Most Sold" value={topQ?.name || "-"} sub={topQ ? `${topQ.qtySold} units` : ""} icon="🔥" gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} delay={160} />
                <Tile label="Top Revenue" value={topR?.name || "-"} sub={topR ? `${topR.revenue} SAR` : ""} icon="🏆" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={240} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 16, marginBottom: 20 }}>
                <RCard title="📊 Top Products by Quantity">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={[...products].sort((a, b) => b.qtySold - a.qtySold).slice(0, 8).map(p => ({ name: p.name, qty: p.qtySold }))} layout="vertical">
                      <GradientDefs />
                      <XAxis type="number" tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                      <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="qty" fill="url(#gCoralBar)" radius={[0, 8, 8, 0]} name="Units" barSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </RCard>
                <RCard title="💰 Top Products by Revenue (SAR)">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={[...products].sort((a, b) => b.revenue - a.revenue).slice(0, 8).map(p => ({ name: p.name, rev: p.revenue }))} layout="vertical">
                      <GradientDefs />
                      <XAxis type="number" tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                      <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="rev" fill="url(#gBlueBar)" radius={[0, 8, 8, 0]} name="SAR" barSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </RCard>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 16, marginBottom: 20 }}>
                <RCard title="🍩 Sales by Category">
                  <ResponsiveContainer width="100%" height={230}>
                    <PieChart>
                      <Pie data={catData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={4} cornerRadius={6}
                        label={({ name, value, cx, x, y }) => (<text x={x} y={y} fill={C.text} fontSize={12} fontWeight={600} textAnchor={x > cx ? "start" : "end"} dominantBaseline="central">{`${name} (${value})`}</text>)}>
                        {catData.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </RCard>
                <RCard title="📈 Daily Trend — Top 4 Products">
                  <ResponsiveContainer width="100%" height={230}>
                    <AreaChart data={productDaily}>
                      <GradientDefs />
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: C.textMed }} axisLine={{ stroke: C.border }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      {top4Names.map((p, i) => (
                        <Area key={p} type="monotone" dataKey={p} stroke={CHART_LINE[i]} fill={CHART_LINE[i]} fillOpacity={0.08} strokeWidth={2} dot={{ r: 2, fill: CHART_LINE[i] }} />
                      ))}
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </RCard>
              </div>

              <RCard title="🏪 Branch Comparison">
                <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden", overflowX: "auto" }}>
                  <table style={tbl.table}>
                    <thead><tr>{["Branch", "Units Sold", "Revenue (SAR)", "Top Product"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead>
                    <tbody>{branchComparison.map(b => (
                      <tr key={b.branch} className="rpt-row">
                        <td style={{ ...tbl.td, fontWeight: 700 }}>{b.branch}</td>
                        <td style={{ ...tbl.td, fontWeight: 600 }}>{b.sold}</td>
                        <td style={{ ...tbl.td, fontWeight: 700, color: C.text }}>{b.rev} SAR</td>
                        <td style={tbl.td}>{b.top}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </RCard>

              <RCard>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>All Products</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {["All", ...categories].map(c => (
                      <div key={c} onClick={() => setCatFilter(c)} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: catFilter === c ? 700 : 500, cursor: "pointer", border: catFilter === c ? `1.5px solid ${C.coral}` : `1.5px solid ${C.border}`, background: catFilter === c ? C.coralLight : C.white, color: catFilter === c ? C.coral : C.textMed, transition: "all 0.12s" }}>{c}</div>
                    ))}
                  </div>
                </div>
                <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                  <table style={tbl.table}>
                    <thead><tr>{["Product", "Category", "Price", "Qty Sold", "Revenue", "Avg/Day", "% Sales", "Top Branch", "Modifiers %"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead>
                    <tbody>{filteredP.map(p => {
                      const topB = Object.entries(p.branches || {}).sort((a, b) => b[1] - a[1])[0];
                      return (
                        <tr key={p.id} className="rpt-row" style={{ cursor: "pointer" }} onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}>
                          <td style={{ ...tbl.td, fontWeight: 700 }}>{p.name}</td>
                          <td style={tbl.td}><Badge color={p.category === "Hot Drinks" ? C.coral : p.category === "Cold Drinks" ? C.blue : C.orange} bg={p.category === "Hot Drinks" ? C.coralLight : p.category === "Cold Drinks" ? C.blueLight : C.orangeLight}>{p.category}</Badge></td>
                          <td style={tbl.td}>{p.price} SAR</td>
                          <td style={{ ...tbl.td, fontWeight: 700 }}>{p.qtySold}</td>
                          <td style={{ ...tbl.td, fontWeight: 700 }}>{p.revenue} SAR</td>
                          <td style={tbl.td}>{p.avgPerDay}</td>
                          <td style={tbl.td}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1, height: 6, background: C.borderLight, borderRadius: 3, overflow: "hidden", minWidth: 60 }}>
                                <div style={{ width: `${totalRev > 0 ? (p.revenue / totalRev) * 100 : 0}%`, height: "100%", background: `linear-gradient(90deg, ${C.coral}, #00bfa5)`, borderRadius: 3, transition: "width 0.6s ease" }} />
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 600, color: C.textMed, whiteSpace: "nowrap" }}>{totalRev > 0 ? ((p.revenue / totalRev) * 100).toFixed(1) : 0}%</span>
                            </div>
                          </td>
                          <td style={tbl.td}>{topB ? `${topB[0]} (${topB[1]})` : "-"}</td>
                          <td style={tbl.td}>{p.withModifiers}%</td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
                {expandedId && (() => {
                  const p = products.find(x => x.id === expandedId);
                  if (!p) return null;
                  return (
                    <div className="rpt-fade" style={{ marginTop: 16, padding: 20, background: `linear-gradient(135deg, ${C.bg}, ${C.white})`, borderRadius: 12, border: `1px solid ${C.border}` }}>
                      <div style={{ fontWeight: 700, marginBottom: 12 }}>Branch Breakdown — {p.name}</div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                        {Object.entries(p.branches || {}).map(([branch, qty]) => (
                          <div key={branch} className="rpt-lift" style={{ background: C.white, padding: 16, borderRadius: 12, border: `1px solid ${C.border}`, textAlign: "center", boxShadow: C.shadow }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.textMed }}>{branch}</div>
                            <div style={{ fontSize: 28, fontWeight: 800, background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginTop: 6 }}>{qty}</div>
                            <div style={{ fontSize: 11, color: C.textLight }}>units sold</div>
                            <div style={{ marginTop: 8, height: 4, background: C.borderLight, borderRadius: 2, overflow: "hidden" }}>
                              <div style={{ width: `${p.qtySold > 0 ? (qty / p.qtySold) * 100 : 0}%`, height: "100%", background: `linear-gradient(90deg, ${C.coral}, #00bfa5)`, borderRadius: 2 }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </RCard>
            </>
          )}
        </div>
      </Container>
    </Fragment>
  );
};

export default ProductPerformancePage;
